import { localizeDateNames } from "@/lib/dateNames";
import i18n from "@/i18n";
/**
 * Prompt 16B-2B2 · Averaging Planning Compliance — presentation.
 *
 * Read-only view of the `planning_compliance` object attached to
 * the manager Averaging summary read (16B-2A2a §4). Preserves the
 * canonical semantic distinction between `WARNING` (concise
 * actionable) and `REVIEW_REQUIRED` (neutral "Needs review") copy
 * so missing absence/vacation data never surfaces as a definitive
 * legal failure (§5, §14).
 *
 * The component NEVER redoes rules client-side and NEVER exposes
 * internal rule codes, period IDs or raw minutes (§4).
 */
import React, { useState } from "react";

const _PASS = "PASS";
const _WARNING = "WARNING";
const _REVIEW = "REVIEW_REQUIRED";

// Prompt 18B2 §13-§18 · §23 — canonical manager-facing human
// strings for absence-aware Averaging planning findings. Frozen
// so tests can assert exact equality and legal wording never
// creeps in (§23 · no "illegal" / "violation" / "TES breach").
export const _MSG_TS_WITH_ANNUAL_LEAVE =
    "TS overlaps annual leave — review the schedule.";
export const _MSG_TS_WITH_ABSENCE_BY_TYPE = {
    get SICK_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.SICK_LEAVE", "TS overlaps sick leave — review required."); },
    get UNPAID_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.UNPAID_LEAVE", "TS overlaps unpaid leave — review required."); },
    get OTHER_APPROVED() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.OTHER_APPROVED", "TS overlaps other approved absence — review required."); },
    get PREGNANCY_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.PREGNANCY_LEAVE", "TS overlaps pregnancy leave — review required."); },
    get PARENTAL_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.PARENTAL_LEAVE", "TS overlaps parental leave — review required."); },
    get TEMPORARY_CHILD_CARE_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.TEMPORARY_CHILD_CARE_LEAVE", "TS overlaps temporary child-care leave — review required."); },
    get OTHER_FAMILY_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.OTHER_FAMILY_LEAVE", "TS overlaps other family leave — review required."); },
    get PAID_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.PAID_LEAVE", "TS overlaps paid leave — review required."); },
    // Never used in practice (ANNUAL_LEAVE routes to the WARNING
    // branch above), but kept for defensive completeness.
    get ANNUAL_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ABSENCE_BY_TYPE.ANNUAL_LEAVE", "TS overlaps annual leave — review the schedule."); },
};
export const _MSG_PLANNED_WORK_ABSENCE_TEMPLATE =
    "Planned work overlaps {absence} on {date} — review the schedule.";
export const _MSG_WEEKLY_V_MISSING_18B2 =
    "Weekly rest day V is missing.";
export const _MSG_WEEKLY_V_REVIEW_ABSENCE =
    "Weekly rest needs review because an absence overlaps this week.";
export const _MSG_X_TRANSFER_FREE_PAIR_REQUIRED_18B2 =
    "Transferred X needs an adjacent free day.";
export const _MSG_X_TRANSFER_FREE_PAIR_WITH_ABSENCE =
    "Transferred X free-day pairing needs review because "
    + "an absence is adjacent.";
export const _MSG_CONSECUTIVE_FREE_REQUIRED_18B2 =
    "Required consecutive free-day combination is missing.";
export const _MSG_CONSECUTIVE_FREE_WITH_ABSENCE =
    "Consecutive free-day requirement needs review because "
    + "an absence affects the relevant dates.";

// Prompt 18B2 §13-§15 — human absence labels for inline messages.
const _ABSENCE_HUMAN = {
    get ANNUAL_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.ANNUAL_LEAVE", "annual leave"); },
    get SICK_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.SICK_LEAVE", "sick leave"); },
    get UNPAID_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.UNPAID_LEAVE", "unpaid leave"); },
    get OTHER_APPROVED() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.OTHER_APPROVED", "other approved absence"); },
    get PREGNANCY_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.PREGNANCY_LEAVE", "pregnancy leave"); },
    get PARENTAL_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.PARENTAL_LEAVE", "parental leave"); },
    get TEMPORARY_CHILD_CARE_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.TEMPORARY_CHILD_CARE_LEAVE", "temporary child-care leave"); },
    get OTHER_FAMILY_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.OTHER_FAMILY_LEAVE", "other family leave"); },
    get PAID_LEAVE() { return i18n.t("wf.PlanningComplianceSummary._ABSENCE_HUMAN.PAID_LEAVE", "paid leave"); },
};


function _formatDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return localizeDateNames(dt.toLocaleDateString("en-GB",
        { day: "numeric", month: "short", timeZone: "UTC" }));
}


function _range(a, b) {
    if (!a && !b) return "";
    if (!b || a === b) return _formatDate(a);
    return `${_formatDate(a)} – ${_formatDate(b)}`;
}


function _actionable(pc) {
    // Prompt 16B-2B2 §4 · §12 — collect ONLY the sections that
    // are non-PASS, in the order the manager needs to see them.
    // Each item is `{ id, label, detail, severity }`. `severity`
    // is either "warning" or "review" — mirrors the canonical
    // backend semantic (§5).
    //
    // Prompt 18B2 §13-§18 · §20 · §23 — additive absence-aware
    // branches. Old branches remain byte-preserved so 16B-2B2
    // historical assertions continue to hold for their pinned
    // reason codes; new absence-aware reason codes emerge as
    // additional items with the frozen §13-§18 wording. No raw
    // backend codes, IDs or minutes appear anywhere.
    const items = [];
    if (!pc) return items;

    // Weekly V — surface only the weeks that are non-PASS.
    const wv = pc.weekly_v;
    if (wv && wv.status !== _PASS && Array.isArray(wv.weeks)) {
        for (const wk of wv.weeks) {
            if (wk.status === _WARNING) {
                items.push({
                    id: `weekly-v-${wk.week_start}`,
                    label: i18n.t("wf.pcs.vMissing", "V missing"),
                    detail: _range(wk.week_start, wk.week_end),
                    severity: "warning",
                });
            } else if (wk.status === _REVIEW) {
                // Prompt 18B2 §16 second part — weekly V REVIEW
                // caused by an ACTIVE absence overlapping the
                // week uses the frozen absence-aware wording.
                if (wk.absence_type) {
                    items.push({
                        id: `weekly-v-review-${wk.week_start}`,
                        label: i18n.t("wf.PlanningComplianceSummary._MSG_WEEKLY_V_REVIEW_ABSENCE", _MSG_WEEKLY_V_REVIEW_ABSENCE),
                        detail: _range(wk.week_start, wk.week_end),
                        severity: "review",
                    });
                } else {
                    items.push({
                        id: `weekly-v-review-${wk.week_start}`,
                        label: i18n.t("wf.pcs.weeklyVReview", "Weekly V — needs review"),
                        detail: _range(wk.week_start, wk.week_end),
                        severity: "review",
                    });
                }
            }
        }
    }

    // Consecutive workdays.
    const cw = pc.consecutive_workdays;
    if (cw && cw.status === _WARNING) {
        items.push({
            id: "consec-workdays",
            label: i18n.t("wf.pcs.consecutiveWorkdays", "{{n}} consecutive workdays", { n: cw.longest_streak }),
            detail: _formatDate(cw.streak_end_date),
            severity: "warning",
        });
    }

    // TS.
    const ts = pc.ts;
    if (ts && ts.minimum_status === _REVIEW && ts.minimum != null) {
        items.push({
            id: "ts-min",
            label: i18n.t("wf.pcs.tsDaysReview", "TS days {{count}} / {{min}} — review", { count: ts.count, min: ts.minimum }),
            detail: i18n.t("wf.pcs.tsRequired", "{{min}} required when hour accumulation permits", { min: ts.minimum }),
            severity: "review",
        });
    }
    if (ts && ts.with_work_status === _REVIEW
            && Array.isArray(ts.conflict_dates)) {
        for (const d of ts.conflict_dates) {
            items.push({
                id: `ts-work-${d}`,
                label: i18n.t("wf.pcs.tsPlannedWork", "TS + planned work — review"),
                detail: _formatDate(d),
                severity: "review",
            });
        }
    }
    // Prompt 18B2 §13 — TS + ANNUAL_LEAVE emerges as a WARNING
    // item per overlap date. Never described as a legal
    // conclusion.
    if (ts && Array.isArray(ts.with_annual_leave_dates)) {
        for (const d of ts.with_annual_leave_dates) {
            items.push({
                id: `ts-annual-${d.date}`,
                label: i18n.t("wf.PlanningComplianceSummary._MSG_TS_WITH_ANNUAL_LEAVE", _MSG_TS_WITH_ANNUAL_LEAVE),
                detail: _formatDate(d.date),
                severity: "warning",
            });
        }
    }
    // Prompt 18B2 §14 — TS + other ACTIVE absence emerges as a
    // "Needs review" item per overlap date, using the specific
    // absence-type human label.
    if (ts && Array.isArray(ts.with_absence_dates)) {
        for (const d of ts.with_absence_dates) {
            const msg = _MSG_TS_WITH_ABSENCE_BY_TYPE[d.absence_type]
                || _MSG_TS_WITH_ABSENCE_BY_TYPE.OTHER_APPROVED;
            items.push({
                id: `ts-absence-${d.date}`,
                label: msg,
                detail: _formatDate(d.date),
                severity: "review",
            });
        }
    }

    // Prompt 18B2 §15 — planned work + ACTIVE absence overlap.
    // One item per overlap date; date is embedded in the copy
    // per the frozen wording.
    const pwa = pc.planned_work_absence_overlap;
    if (pwa && pwa.status === _REVIEW
            && Array.isArray(pwa.dates)) {
        for (const d of pwa.dates) {
            const human = _ABSENCE_HUMAN[d.absence_type]
                || _ABSENCE_HUMAN.OTHER_APPROVED;
            const label = i18n.t("wf.PlanningComplianceSummary._MSG_PLANNED_WORK_ABSENCE_TEMPLATE", _MSG_PLANNED_WORK_ABSENCE_TEMPLATE)
                .replace("{absence}", human)
                .replace("{date}", _formatDate(d.date));
            items.push({
                id: `planned-work-absence-${d.date}`,
                label,
                detail: "",
                severity: "review",
            });
        }
    }

    // Transferred X free-pair.
    // Prompt 18B2 §17 — split by entry status/reason. WARNING
    // entries use the "adjacent free day" wording; REVIEW
    // entries whose reason is the canonical adjacent-absence
    // sentinel use the frozen absence-aware wording; other
    // REVIEW entries retain the historical wording so 16B-2B2
    // assertions still hold.
    const xt = pc.x_transfers;
    if (xt && Array.isArray(xt.entries)) {
        for (const e of xt.entries) {
            if (e.status === _WARNING) {
                items.push({
                    id: `xt-${e.day_entry_id}`,
                    label: i18n.t("wf.PlanningComplianceSummary._MSG_X_TRANSFER_FREE_PAIR_REQUIRED_18B2", _MSG_X_TRANSFER_FREE_PAIR_REQUIRED_18B2),
                    detail: _formatDate(e.date),
                    severity: "warning",
                });
            } else if (e.status === _REVIEW) {
                if (e.reason
                    === "AVERAGING_X_TRANSFER_FREE_PAIR_WITH_"
                        + "ABSENCE_REVIEW_REQUIRED") {
                    items.push({
                        id: `xt-${e.day_entry_id}`,
                        label: i18n.t("wf.PlanningComplianceSummary._MSG_X_TRANSFER_FREE_PAIR_WITH_ABSENCE", _MSG_X_TRANSFER_FREE_PAIR_WITH_ABSENCE),
                        detail: _formatDate(e.date),
                        severity: "review",
                    });
                } else {
                    items.push({
                        id: `xt-${e.day_entry_id}`,
                        label: i18n.t("wf.pcs.transferredXFreeDay", "Transferred X needs another free day"),
                        detail: _formatDate(e.date),
                        severity: "review",
                    });
                }
            }
        }
    }

    // Consecutive free days.
    // Prompt 18B2 §18 — WARNING gets the "required combination
    // missing" wording; REVIEW+absence gets the frozen
    // absence-aware wording; other REVIEW cases keep the
    // historical wording so 16B-2B2 assertions hold.
    const cfd = pc.consecutive_free_days;
    if (cfd && cfd.status === _WARNING) {
        items.push({
            id: "cfd",
            label: i18n.t("wf.PlanningComplianceSummary._MSG_CONSECUTIVE_FREE_REQUIRED_18B2", _MSG_CONSECUTIVE_FREE_REQUIRED_18B2),
            detail: "",
            severity: "warning",
        });
    } else if (cfd && cfd.status === _REVIEW) {
        if (cfd.reason
            === "AVERAGING_CONSECUTIVE_FREE_COMBINATION_WITH_"
                + "ABSENCE_REVIEW_REQUIRED") {
            items.push({
                id: "cfd",
                label: i18n.t("wf.PlanningComplianceSummary._MSG_CONSECUTIVE_FREE_WITH_ABSENCE", _MSG_CONSECUTIVE_FREE_WITH_ABSENCE),
                detail: "",
                severity: "review",
            });
        } else {
            items.push({
                id: "cfd",
                label: i18n.t("wf.pcs.consecutiveFreeReview", "Consecutive free days — needs review"),
                detail: "",
                severity: "review",
            });
        }
    }

    return items;
}


/**
 * Compact Planning compliance surface inside the profile
 * Averaging section (§3, §4). No warning icons; no legal text.
 */
export function PlanningComplianceSummary({ planningCompliance }) {
    const [expanded, setExpanded] = useState(false);
    const pc = planningCompliance;
    if (!pc) {
        return (
            <div
                className="text-sm text-neutral-500"
                data-testid="planning-compliance-none"
            >
                —
            </div>
        );
    }
    const status = pc.status;
    const items = _actionable(pc);
    const passSummary = i18n.t("wf.pcs.onTrack", "On track");
    let headline;
    if (status === _PASS) {
        headline = passSummary;
    } else if (items.length === 1) {
        headline = i18n.t("wf.pcs.oneItem", "1 item to review");
    } else {
        headline = i18n.t("wf.pcs.nItems", "{{n}} items to review", { n: items.length });
    }

    return (
        <div
            className="rounded-md border border-neutral-200
                        bg-white p-3"
            data-testid="planning-compliance-summary"
        >
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-neutral-700">
                    {i18n.t("wf.planningComplianceSummary.planning", "Planning")}
                </div>
                <button
                    type="button"
                    data-testid="planning-compliance-status"
                    data-planning-status={status}
                    className="text-xs text-neutral-600
                                hover:text-neutral-900"
                    onClick={() => setExpanded((v) => !v)}
                    disabled={status === _PASS}
                >
                    {status === _PASS ? (
                        <span data-testid="planning-compliance-ontrack">
                            ✓ {passSummary}
                        </span>
                    ) : (
                        <span data-testid="planning-compliance-review-count">
                            {headline}
                        </span>
                    )}
                </button>
            </div>
            {expanded && items.length > 0 && (
                <ul
                    className="mt-2 space-y-1 text-sm text-neutral-700"
                    data-testid="planning-compliance-details"
                >
                    {items.map((it) => (
                        <li
                            key={it.id}
                            data-testid={`planning-item-${it.id}`}
                            data-severity={it.severity}
                            className="flex justify-between gap-4"
                        >
                            <span>{it.label}</span>
                            {it.detail ? (
                                <span className="text-neutral-500 text-xs">
                                    {it.detail}
                                </span>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}


export default PlanningComplianceSummary;
